import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Breadcrumbs, 
  Link, 
  Button, 
  Grid, 
  Card, 
  CardContent,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Link as MuiLink,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Dialog,
  DialogContent,
  DialogActions,
  CardActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MemoryIcon from '@mui/icons-material/Memory';
import CircleIcon from '@mui/icons-material/Circle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { RaspberryPi } from '../../model/RaspberryPi';
import { PiService } from '../../service/piService';
import RaspberryPiDetail from '../../components/RaspberryPiDetail';

const RaspberryPiAdmin: React.FC = () => {
  const [pis, setPis] = useState<RaspberryPi[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [creating, setCreating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [loadingWireGuard, setLoadingWireGuard] = useState<Record<string, boolean>>({});
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const navigate = useNavigate();
  
  // State für das Detail-Modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedPiId, setSelectedPiId] = useState<string | null>(null);
  const [selectedPi, setSelectedPi] = useState<RaspberryPi | null>(null);

  // State für erweiterte WireGuard-Konfigurationen
  const [wireGuardConfigs, setWireGuardConfigs] = useState<Record<string, any>>({});

  // Lade alle Raspberry Pis beim ersten Rendern
  useEffect(() => {
    fetchPis();
  }, []);

  // Lade gespeicherte WireGuard-Konfigurationen aus dem localStorage
  useEffect(() => {
    try {
      const storedConfigs = localStorage.getItem('wireguardConfigs');
      if (storedConfigs) {
        const parsedConfigs = JSON.parse(storedConfigs);
        setWireGuardConfigs(parsedConfigs);
      }
    } catch (error) {
      console.error('Fehler beim Laden der WireGuard-Konfigurationen aus dem localStorage:', error);
    }
  }, []);

  // Speichere WireGuard-Konfigurationen für alle Pis im localStorage
  useEffect(() => {
    if (pis.length > 0) {
      const newConfigs = { ...wireGuardConfigs };
      let hasChanges = false;

      pis.forEach(pi => {
        // Überprüfe, ob dieser Pi WireGuard-Daten hat
        const hasWireguardData = pi.wireguardClientPublicKey || pi.wireguardClientPrivateKey || 
                                pi.wireguardServerPublicKey || pi.wireguardServerPrivateKey || 
                                pi.wireguardPresharedKey;
        
        if (pi._id && hasWireguardData) {
          // Speichere alle verfügbaren WireGuard-Daten, auch wenn nur teilweise vorhanden
          const piConfig = {
            wireguardClientPublicKey: pi.wireguardClientPublicKey,
            wireguardClientPrivateKey: pi.wireguardClientPrivateKey,
            wireguardServerPublicKey: pi.wireguardServerPublicKey,
            wireguardServerPrivateKey: pi.wireguardServerPrivateKey,
            wireguardPresharedKey: pi.wireguardPresharedKey,
            wireguardPort: pi.wireguardPort || 51820,
            bridgeNetworkBlock: pi.bridgeNetworkBlock || '10.13.13.0/24',
            lastUpdated: new Date().toISOString()
          };
          
          newConfigs[pi._id] = piConfig;
          hasChanges = true;
        }
      });

      if (hasChanges) {
        setWireGuardConfigs(newConfigs);
        localStorage.setItem('wireguardConfigs', JSON.stringify(newConfigs));
      }
    }
  }, [pis]);

  // Lade alle Raspberry Pis vom Server
  const fetchPis = async () => {
    try {
      setLoading(true);
      const loadedPis = await PiService.getPis();
      setPis(loadedPis);
    } catch (err) {
      console.error('Fehler beim Laden der Raspberry Pis:', err);
      setError('Die Raspberry Pis konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  // Generiere einen zufälligen süßen Namen für Raspberry Pi
  const generateCuteName = (): string => {
    const adjectives = [
      'Süßer', 'Kleiner', 'Fleißiger', 'Schneller', 'Smarter', 
      'Freundlicher', 'Flauschiger', 'Fröhlicher', 'Niedlicher', 'Bunter',
      'Kreativer', 'Cleverer', 'Mutiger', 'Fleißiger', 'Schlafer',
      'Neugieriger', 'Lustiger', 'Energischer', 'Entspannter', 'Schlauer',
      'Starker', 'Ruhiger', 'Glücklicher', 'Kluger', 'Wacher',
      'Rasanter', 'Eifriger', 'Aktiver', 'Leiser', 'Höflicher',
      'Präziser', 'Zuverlässiger', 'Sicherer', 'Freier', 'Wilder',
      'Dynamischer', 'Innovativer', 'Optimistischer', 'Geselliger', 'Sanfter'
    ];
    
    const nouns = [
      'Igel', 'Panda', 'Fuchs', 'Pinguin', 'Koala', 
      'Hase', 'Otter', 'Hamster', 'Drache', 'Puma',
      'Tiger', 'Löwe', 'Elefant', 'Delfin', 'Eichhörnchen',
      'Bär', 'Wolf', 'Adler', 'Frosch', 'Katze',
      'Dachs', 'Biber', 'Luchs', 'Waschbär', 'Reh',
      'Falke', 'Leopard', 'Chamäleon', 'Kolibri', 'Pferd',
      'Gepard', 'Bison', 'Hirsch', 'Faultier', 'Schwan',
      'Affe', 'Känguru', 'Zebra', 'Lama', 'Papagei'
    ];
    
    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
    
    // Keine Zufallszahl mehr hinzufügen, um den Namen kürzer und sauberer zu halten
    return `${randomAdjective} ${randomNoun}`;
  };

  // Erstelle einen neuen Raspberry Pi
  const handleGenerateExample = async () => {
    try {
      setCreating(true);
      setError(null);
      
      // Erstelle ein zufälliges Beispiel für einen Raspberry Pi
      const serialNumber = `RP${Math.floor(1000000 + Math.random() * 9000000)}`;
      // Keine IP-Adresse initial setzen
      const cuteName = generateCuteName();
      
      // Generiere die Wireguard-Konfiguration
      const wireguardConfig = PiService.generateWireguardConfig();
      
      // Generiere eine Subdomain für den Pi
      const subdomain = PiService.generateSubdomain(cuteName, serialNumber);
      console.log(`Generierte Subdomain für neuen Pi: ${subdomain}`);
      
      // Credentials erstellen mit eindeutigem Passwort
      const credentials = {
        username: 'admin',
        password: `pass_${Date.now().toString(36)}`
      };
      
      // Erstelle den Raspberry Pi in der Datenbank
      const newPi = await PiService.createPi({
        name: cuteName,
        serialNumber: serialNumber,
        status: 'created',
        ipAddress: "", // Leere String wird in piService gefiltert
        subdomain: subdomain, // Subdomain direkt mit angeben
        isPrepared: false, // Initial nicht vorbereitet
        credentials: credentials,
        // Wireguard-Konfiguration hinzufügen
        wireguardPort: wireguardConfig.wireguardPort,
        bridgeNetworkBlock: wireguardConfig.bridgeNetworkBlock,
        // Setze alle Schlüssel explizit auf null, damit sie vom Backend generiert werden können
        wireguardPresharedKey: null,
        wireguardClientPublicKey: null,
        wireguardClientPrivateKey: null,
        wireguardServerPublicKey: null,
        wireguardServerPrivateKey: null
      });
      
      // Stellen Sie sicher, dass newPi._id existiert, bevor Sie es zur Liste hinzufügen
      if (!newPi._id) {
        console.warn('Neu erstellter Pi hat keine gültige ID');
        newPi._id = newPi.id || `temp-${Date.now()}`;
      }
      
      // Aktualisiere die Liste - setze neuen Pi an erste Stelle
      setPis([newPi, ...pis]);
      setSuccess(`Raspberry Pi "${newPi.name}" erfolgreich erstellt und in der Datenbank gespeichert.`);
      
      // Automatisch schließen nach 5 Sekunden
      setTimeout(() => {
        setSuccess(null);
      }, 5000);
      
    } catch (err: any) {
      console.error('Fehler beim Erstellen des Raspberry Pi:', err);
      
      // Verbesserte Fehlerbehandlung für verschiedene Fehlerarten
      let errorMessage = 'Der Raspberry Pi konnte nicht erstellt werden. Bitte versuchen Sie es später erneut.';
      
      if (err.message && err.message.includes('API-Validierungsfehler')) {
        // Bei API-Validierungsfehlern zeigen wir die spezifische Fehlermeldung an
        // Entferne das "API-Validierungsfehler: " Präfix für eine benutzerfreundlichere Anzeige
        errorMessage = err.message.replace('API-Validierungsfehler: ', '');
        
        // Wenn es sich um ein Problem mit der IP-Adresse handelt, geben wir einen spezifischeren Hinweis
        if (errorMessage.toLowerCase().includes('ip address') || errorMessage.toLowerCase().includes('ipaddress')) {
          errorMessage += ' Hinweis: Möglicherweise muss das Backend angepasst werden, um leere IP-Adressen zu akzeptieren.';
        }
      } else if (err.response && err.response.status === 400) {
        // Bei 400-Fehlern ohne spezifische Fehlermeldung
        if (err.response.data && err.response.data.errors && Array.isArray(err.response.data.errors) && err.response.data.errors.length > 0) {
          // Zeige den ersten Validierungsfehler
          errorMessage = err.response.data.errors[0].msg || 'Die Anfrage enthält ungültige Daten.';
        } else {
          errorMessage = err.response.data?.message || 'Die Anfrage enthält ungültige Daten.';
        }
      } else if (err.response && err.response.status === 401) {
        // Bei Authentifizierungsproblemen
        errorMessage = 'Nicht autorisiert. Bitte melden Sie sich erneut an.';
      } else if (err.response && err.response.status === 500) {
        // Bei Serverproblemen
        errorMessage = 'Serverfehler beim Erstellen des Raspberry Pi. Bitte versuchen Sie es später erneut.';
      } else if (err.message) {
        // Wenn eine Nachricht existiert, verwenden wir diese
        errorMessage = `Fehler: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  // Kopiere Text in die Zwischenablage
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(`${label} wurde in die Zwischenablage kopiert`);
      setTimeout(() => setCopySuccess(null), 2000);
    }, (err) => {
      console.error('Fehler beim Kopieren:', err);
    });
  };

  // Status-Farbe basierend auf dem Status ermitteln
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'online':
        return '#30D158'; // Grün
      case 'offline':
        return '#FF3B30'; // Rot
      case 'warning':
        return '#FF9500'; // Orange
      case 'created':
        return '#007AFF'; // Blau
      case 'ready_for_shipping':
        return '#5856D6'; // Lila
      case 'out_for_delivery':
        return '#AF52DE'; // Pink
      case 'installed':
        return '#34C759'; // Grün
      default:
        return '#8E8E93'; // Grau
    }
  };

  // Status-Text basierend auf dem Status ermitteln
  const getStatusText = (status: string): string => {
    switch (status) {
      case 'online':
        return 'Online';
      case 'offline':
        return 'Offline';
      case 'warning':
        return 'Warnung';
      case 'created':
        return 'Erstellt';
      case 'ready_for_shipping':
        return 'Versandfertig';
      case 'out_for_delivery':
        return 'Unterwegs';
      case 'installed':
        return 'Installiert';
      default:
        return 'Unbekannt';
    }
  };

  // Formatiere das Datum für bessere Lesbarkeit
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (e) {
      return 'Unbekannt';
    }
  };

  // Laden eines einzelnen Pi mit vollständigen WireGuard-Daten
  const loadPi = async (piId: string) => {
    if (!piId) return;
    
    try {
      // Setze Ladestatus für diesen Pi
      setLoadingWireGuard(prev => ({ ...prev, [piId]: true }));
      
      console.log(`Lade WireGuard-Konfiguration für Pi ${piId}...`);
      const loadedPi = await PiService.getPiById(piId);
      
      // Aktualisiere den Pi in der Liste
      setPis(prevPis => prevPis.map(pi => pi._id === piId ? loadedPi : pi));
      
      // Überprüfe, ob die WireGuard-Konfiguration jetzt vollständig ist
      const isComplete = Boolean(loadedPi.wireguardClientPublicKey) && 
                         Boolean(loadedPi.wireguardServerPublicKey);
      
      if (isComplete) {
        console.log(`WireGuard-Konfiguration für Pi ${piId} erfolgreich geladen.`);
        // Aktualisiere auch den lokalen Cache
        const newConfigs = { ...wireGuardConfigs };
        if (loadedPi._id) {
          newConfigs[loadedPi._id] = {
            wireguardClientPublicKey: loadedPi.wireguardClientPublicKey,
            wireguardClientPrivateKey: loadedPi.wireguardClientPrivateKey,
            wireguardServerPublicKey: loadedPi.wireguardServerPublicKey,
            wireguardServerPrivateKey: loadedPi.wireguardServerPrivateKey,
            wireguardPresharedKey: loadedPi.wireguardPresharedKey,
            wireguardPort: loadedPi.wireguardPort || 51820,
            bridgeNetworkBlock: loadedPi.bridgeNetworkBlock || '10.13.13.0/24',
            lastUpdated: new Date().toISOString()
          };
          setWireGuardConfigs(newConfigs);
          localStorage.setItem('wireguardConfigs', JSON.stringify(newConfigs));
        }
      } else {
        console.warn(`WireGuard-Konfiguration für Pi ${piId} konnte nicht vollständig geladen werden.`);
      }
    } catch (err) {
      console.error(`Fehler beim Laden des Pi ${piId}:`, err);
    } finally {
      // Ladestatus zurücksetzen
      setLoadingWireGuard(prev => ({ ...prev, [piId]: false }));
    }
  };

  // Helfer-Funktion zum Abrufen der WireGuard-Konfiguration für einen bestimmten Pi
  const getWireGuardConfig = (pi: RaspberryPi) => {
    // Erstelle die Basiskonfiguration aus den Pi-Daten
    let config = {
      wireguardClientPublicKey: pi.wireguardClientPublicKey || null,
      wireguardClientPrivateKey: pi.wireguardClientPrivateKey || null,
      wireguardServerPublicKey: pi.wireguardServerPublicKey || null,
      wireguardServerPrivateKey: pi.wireguardServerPrivateKey || null,
      wireguardPresharedKey: pi.wireguardPresharedKey || null,
      wireguardPort: pi.wireguardPort || 51820,
      bridgeNetworkBlock: pi.bridgeNetworkBlock || '10.13.13.0/24'
    };
    
    // Lade-Status für diesen Pi abrufen
    const isLoading = pi._id ? loadingWireGuard[pi._id] : false;
    
    let source = 'api';
    
    // Prüfe, ob die wichtigsten Schlüssel definiert und nicht null sind
    // Wir prüfen auf echte Schlüssel, nicht nur auf das Vorhandensein der Eigenschaft
    const hasClientKey = Boolean(config.wireguardClientPublicKey);
    const hasServerKey = Boolean(config.wireguardServerPublicKey);
    const hasPresharedKey = Boolean(config.wireguardPresharedKey);
    
    let isComplete = hasClientKey && hasServerKey;
    
    // Wenn wir keine vollständigen Schlüssel vom API haben, versuche den lokalen Cache
    if (!isComplete && pi._id && wireGuardConfigs[pi._id]) {
      const storedConfig = wireGuardConfigs[pi._id];
      
      // Ergänze alle fehlenden Felder aus dem localStorage
      if (!hasClientKey && storedConfig.wireguardClientPublicKey) {
        config.wireguardClientPublicKey = storedConfig.wireguardClientPublicKey;
      }
      
      // Client Private Key aus dem localStorage laden
      if (!config.wireguardClientPrivateKey && storedConfig.wireguardClientPrivateKey) {
        config.wireguardClientPrivateKey = storedConfig.wireguardClientPrivateKey;
      }
      
      if (!hasServerKey && storedConfig.wireguardServerPublicKey) {
        config.wireguardServerPublicKey = storedConfig.wireguardServerPublicKey;
      }
      
      if (!hasPresharedKey && storedConfig.wireguardPresharedKey) {
        config.wireguardPresharedKey = storedConfig.wireguardPresharedKey;
      }
      
      // Wenn wir Daten aus dem localStorage verwenden, setze die Quelle entsprechend
      if (storedConfig.wireguardClientPublicKey || storedConfig.wireguardServerPublicKey) {
        source = 'localStorage';
      }
    }
    
    // Prüfe, ob wir jetzt eine vollständige Konfiguration haben
    const hasUpdatedClientKey = Boolean(config.wireguardClientPublicKey);
    const hasUpdatedServerKey = Boolean(config.wireguardServerPublicKey);
    isComplete = hasUpdatedClientKey && hasUpdatedServerKey;
    
    // Debug-Ausgabe, nur wenn es ein tatsächliches Problem gibt
    if (!isComplete && pi._id && !isLoading) {
      console.log(`Pi ${pi._id} (${pi.name}): WireGuard-Konfiguration unvollständig. ClientKey: ${hasUpdatedClientKey}, ServerKey: ${hasUpdatedServerKey}`);
    }
    
    return {
      ...config,
      source,
      isComplete,
      isLoading
    };
  };

  // In the component, add a function to handle the checkbox changes:
  const handlePreparedChange = async (piId: string, newValue: boolean) => {
    // Überprüfe, ob eine gültige Pi-ID vorhanden ist
    if (!piId || piId === 'undefined') {
      console.error(`Ungültige Pi-ID für Statusänderung: "${piId}"`);
      setError('Die Statusänderung konnte nicht durchgeführt werden: Ungültige Raspberry Pi ID');
      return;
    }
    
    try {
      // Optimistische UI-Aktualisierung
      setPis(prevPis => prevPis.map(pi => 
        pi._id === piId ? { ...pi, isPrepared: newValue } : pi
      ));
      
      // Aktualisiere in der Datenbank
      await PiService.updatePiPreparedStatus(piId, newValue);
      
      // Erfolgsmeldung anzeigen
      setSuccess(`Status für Raspberry Pi wurde auf ${newValue ? 'vorbereitet' : 'nicht vorbereitet'} gesetzt.`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Vorbereitungsstatus:', error);
      setError('Der Status konnte nicht aktualisiert werden. Bitte versuchen Sie es später erneut.');
      
      // UI zurücksetzen auf den vorherigen Status
      setPis(prevPis => prevPis.map(pi => 
        pi._id === piId ? { ...pi, isPrepared: !newValue } : pi
      ));
    }
  };

  // Aktualisiere den Status eines Raspberry Pi
  const handleStatusChange = async (piId: string, newStatus: RaspberryPi['status']) => {
    try {
      // Finde den Pi in der Liste
      const piIndex = pis.findIndex(pi => pi._id === piId);
      if (piIndex === -1) {
        console.error(`Pi mit ID ${piId} nicht gefunden`);
        return;
      }

      // Zeige Ladeindikator
      setLoading(true);
      setError(null);

      // Aktualisiere den Status über den Service
      await PiService.updatePiStatus(piId, newStatus);
      
      // Aktualisiere den Pi in der lokalen Liste
      const updatedPis = [...pis];
      updatedPis[piIndex] = {
        ...updatedPis[piIndex],
        status: newStatus
      };
      setPis(updatedPis);

      // Zeige Erfolgsmeldung
      setSuccess(`Status des Raspberry Pi wurde erfolgreich auf "${getStatusText(newStatus)}" geändert.`);
      
      // Automatisch schließen nach 3 Sekunden
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Fehler beim Aktualisieren des Status:', err);
      setError('Der Status konnte nicht aktualisiert werden. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  // Funktion zum Öffnen des Detail-Modals
  const handleOpenDetailModal = async (piId: string) => {
    setSelectedPiId(piId);
    
    // Lade die Pi-Details, falls sie noch nicht geladen sind
    try {
      const piDetails = await PiService.getPiById(piId);
      setSelectedPi(piDetails);
      setDetailModalOpen(true);
    } catch (error) {
      console.error('Fehler beim Laden der Pi-Details:', error);
      setError('Die Details des Raspberry Pi konnten nicht geladen werden.');
    }
  };

  // Funktion zum Schließen des Detail-Modals
  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    // Kurze Verzögerung, bevor wir die Daten zurücksetzen
    setTimeout(() => {
      setSelectedPi(null);
      setSelectedPiId(null);
    }, 300);
  };

  // Funktion zum Aktualisieren der Daten nach Änderungen im Detail-Modal
  const handleDetailUpdate = async () => {
    // Aktualisiere die Pi-Liste, nachdem Änderungen vorgenommen wurden
    if (selectedPiId) {
      try {
        const updatedPi = await PiService.getPiById(selectedPiId);
        
        // Aktualisiere den Pi in der Liste
        setPis(prevPis => prevPis.map(pi => 
          pi._id === selectedPiId ? updatedPi : pi
        ));
        
        // Aktualisiere auch den ausgewählten Pi
        setSelectedPi(updatedPi);
      } catch (error) {
        console.error('Fehler beim Aktualisieren der Pi-Details:', error);
      }
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs Navigation */}
      <Breadcrumbs aria-label="Navigationspfad" sx={{ mb: 2 }}>
        <MuiLink 
          color="inherit" 
          component="button"
          variant="body1"
          onClick={() => navigate('/dashboard')}
          sx={{ textDecoration: 'none' }}
        >
          Dashboard
        </MuiLink>
        <MuiLink 
          color="inherit" 
          component="button"
          variant="body1"
          onClick={() => navigate('/backoffice')}
          sx={{ textDecoration: 'none' }}
        >
          Administration
        </MuiLink>
        <Typography color="text.primary">Raspberry Pi Verwaltung</Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            sx={{ mr: 2 }}
            onClick={() => navigate('/backoffice')}
            aria-label="Zurück zur Administration"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Raspberry Pi Verwaltung
          </Typography>
        </Box>
      </Box>

      {/* Feedback Messages */}
      <Snackbar 
        open={!!success} 
        autoHideDuration={5000} 
        onClose={() => setSuccess(null)}
        sx={{ bottom: { xs: 90, sm: 24 } }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={!!error} 
        autoHideDuration={10000} 
        onClose={() => setError(null)}
        sx={{ bottom: { xs: 90, sm: 24 } }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!copySuccess}
        autoHideDuration={2000}
        onClose={() => setCopySuccess(null)}
        sx={{ bottom: { xs: 140, sm: 80 } }}
      >
        <Alert onClose={() => setCopySuccess(null)} severity="info" sx={{ width: '100%' }}>
          {copySuccess}
        </Alert>
      </Snackbar>

      {/* Content */}
      <Box sx={{ mt: 3 }}>
      {loading ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            py: 8 
          }}>
            <CircularProgress size={60} sx={{ mb: 4 }} />
            <Typography>Lade Raspberry Pis...</Typography>
        </Box>
      ) : (
          viewMode === 'cards' ? (
            // Kartenansicht
            <Grid container spacing={4} sx={{ mt: 2 }}>
              {pis.map(pi => (
                <Grid item xs={12} sm={6} md={4} key={pi._id} sx={{ mb: 2 }}>
                  <Card sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
                    },
                    mb: 4
                  }}>
                    <CardContent sx={{ flexGrow: 1, p: 2 }}>
                      {/* Pi Name und Status */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <MemoryIcon sx={{ color: 'primary.main', mr: 1 }} />
                          <Typography variant="h6" component="div" sx={{ fontSize: '1rem' }}>
                            {pi.name}
                          </Typography>
                        </Box>
                        <Chip 
                          icon={<CircleIcon sx={{ fontSize: '0.8rem !important' }} />}
                          label={getStatusText(pi.status)}
                          size="small"
                          sx={{ 
                            backgroundColor: `${getStatusColor(pi.status)}20`,
                            color: getStatusColor(pi.status),
                            fontWeight: 'bold',
                            '& .MuiChip-icon': {
                              color: getStatusColor(pi.status),
                              marginLeft: '8px',
                            }
                          }}
                        />
                      </Box>
                    
                      {/* Pi Details */}
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Seriennummer:</strong> {pi.serialNumber}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>IP-Adresse:</strong> {pi.ipAddress || 'Nicht zugewiesen'}
                      </Typography>
                        
                      {/* Zuweisungs Status */}
                      <Typography variant="body2" color="text.secondary">
                        <strong>Zugewiesen an:</strong> {pi.ownerName || 'Nicht zugewiesen'}
                      </Typography>
                    </CardContent>
                      
                    {/* Aktionsbuttons */}
                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => handleOpenDetailModal(pi._id)}
                        sx={{ backgroundColor: 'black', color: 'white' }}
                      >
                        Details
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            // Tabellenansicht
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="raspberry pi table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Seriennummer</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Zugewiesen an</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pis.map((pi) => (
                    <TableRow key={pi._id} hover>
                      <TableCell><strong>{pi.name}</strong></TableCell>
                      <TableCell>{pi.serialNumber}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip 
                            label={getStatusText(pi.status)} 
                            sx={{ 
                              backgroundColor: getStatusColor(pi.status),
                              color: 'white',
                              fontWeight: 'medium'
                            }} 
                            size="small"
                          />
                          {/* Status-Tabs */}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              handleOpenDetailModal(pi._id);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {pi.ownerName || 'Nicht zugewiesen'}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleOpenDetailModal(pi._id)}
                        >
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}
      </Box>
      
      {/* Pi-Detail-Modal */}
      <Dialog 
        open={detailModalOpen} 
        onClose={handleCloseDetailModal}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton edge="end" color="inherit" onClick={handleCloseDetailModal} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {selectedPi && (
            <RaspberryPiDetail 
              pi={selectedPi} 
              onBackClick={handleCloseDetailModal}
              onUpdate={handleDetailUpdate}
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default RaspberryPiAdmin; 